import { _GettersTree, defineStore } from 'pinia';
import { Pupil } from "@/models/Pupil.model";
import { Icon, FileIcon, SpriteIcon } from "@/models/Icon.model";
import { ApiErrorMessageBag } from "@/stores/errors/ApiErrorMessageBag.ts";
import { OrderDirection, OrderParameter } from "@/models/OrderParameter.model";
import { useDefaultOrderStore } from "@/stores/DefaultOrder.store";
import { Classroom } from "@/models/Classroom.model";
import { useClassroomsStore } from "@/stores/Classrooms.store";
import { DateTime } from 'luxon';
import { PupilFilterDef } from "@/stores/filters/PupilFilterDef";
import { AbstractBaseStore, AbstractStoreState, Method } from "@/stores/AbstractBase.store";

interface PupilsStoreState extends AbstractStoreState<Pupil, PupilFilterDef> {
	// Map of icon identifier (sprite url or file id) -> pupil currently using that icon.
	// Only populated while a single classroom is in scope (see iconMapClassroom).
	usedIconsMap: { [key: string | number]: Pupil | null },

	// The classroom for which usedIconsMap is built. null = no single-classroom scope,
	// so the icon-usage map is not maintained.
	iconMapClassroom: Classroom | null,

	// Dedup key for loadForClassroom() so repeated calls for the same scope reuse the
	// in-flight / completed load instead of re-fetching.
	loadedClassroomKey: string | null,
	_loadForClassroomPromise: Promise<void> | null,
	_loadAllMode: boolean,

	// Dedup key + promise for loadAllForClassroom(), so concurrent callers for the same
	// scope share one page-loading loop and a different scope supersedes a running one.
	_loadAllForClassroomKey: string | null,
	_loadAllForClassroomPromise: Promise<Pupil[]> | null,
}

const BaseStore = AbstractBaseStore<Pupil, PupilsStoreState, PupilFilterDef>();

export const usePupilsStore = defineStore('pupils', {

	state: (): PupilsStoreState => {
		return BaseStore.state({
			order: useDefaultOrderStore().pupilsOrder,
			defaultFilter: new PupilFilterDef(),
			initialFilter: new PupilFilterDef(),

			usedIconsMap: {},
			iconMapClassroom: null,
			loadedClassroomKey: null,
			_loadForClassroomPromise: null,
			_loadAllForClassroomKey: null,
			_loadAllForClassroomPromise: null,
			_loadAllMode: false,
		});
	},

	getters: {

		...BaseStore.getters,

		apiPath() {
			return 'pupils';
		},

		defaultMask(): string[] {
			return [
				'id',
				'color',
				'firstName',
				'lastName',
				'symbol',
				'birthdate',
				'classrooms.*',
				'classrooms.icon.*',
				'classrooms.icon.library.id',
				'files.*',
				'files.variations.*',
			];
		},

		// Pupils are paginated in the UI; don't load the full set in one request.
		defaultRecordsPerPage(): number {
			return 50;
		},

		isIconUsed: (state) => {
			return (icon: Icon) => {
				if (icon instanceof SpriteIcon && icon.url && typeof (state.usedIconsMap[icon.url]) !== 'undefined') {
					return true;
				} else if (icon instanceof FileIcon && icon.id && typeof (state.usedIconsMap[icon.id]) !== 'undefined') {
					return true;
				} else {
					return false;
				}
			};
		},

	} as _GettersTree<PupilsStoreState>,

	actions: {

		...BaseStore.actions,

		mapToItem(data: any): Pupil {
			return Pupil.createFromApiData(data);
		},

		/**
		 * Reset the store to its empty state.
		 * Called e.g. when switching classroom to avoid showing stale data while the page reloads.
		 */
		clear() {
			this.items = [];
			this.usedIconsMap = {};
			this.iconMapClassroom = null;
			this.loadedOnce = false;
			this.loadedClassroomKey = null;
			this._loadForClassroomPromise = null;
			this._loadAllMode = false;
			this._loadAllForClassroomKey = null;
			this._loadAllForClassroomPromise = null;
		},

		/**
		 * Create a new (unsaved) pupil, optionally pre-assigned to a classroom.
		 */
		async new(classroom: Classroom | null = null): Promise<Pupil> {
			const newPupil = new Pupil();
			if (classroom !== null) {
				await newPupil.toggleClassroom(classroom, true);
			}
			return newPupil;
		},

		/**
		 * Scope the store to a single classroom (or all classrooms when null) and load it.
		 *
		 * Classroom scoping is expressed through the filter's `classroomIds` parameter, so this
		 * delegates to the standard filter/load flow of the base store. Repeated calls for the same
		 * scope are deduplicated (unless `force` is set), which mirrors the old loadedClassroom cache.
		 *
		 * @param classroom The classroom to scope to. `undefined` (default) uses the current
		 *                   classroom; `null` loads pupils across all classrooms.
		 * @param force      Reload even if the requested scope is already loaded / loading.
		 */
		async loadForClassroom(
			classroom: Classroom | null | undefined = undefined,
			force: boolean = false
		): Promise<void> {
			if (typeof (classroom) === 'undefined') {
				classroom = useClassroomsStore().currentClassroom;
			}

			const key = classroom ? `classroom:${classroom.id}` : 'all';

			if (!force && this.loadedClassroomKey === key) {
				// Already loaded (or loading) for this exact scope.
				return this._loadForClassroomPromise ?? Promise.resolve();
			}

			// This is the paginated load path, so leave "load all pages" mode.
			// loadAllForClassroom() re-enables it after delegating here, so that
			// re-ordering keeps reloading every page on the screens that use it.
			this._loadAllMode = false;

			this.loadedClassroomKey = key;

			// The icon-usage map is only meaningful when viewing a single classroom.
			this.iconMapClassroom = classroom ?? null;

			const filter = this.getDefaultFilter();
			if (classroom) {
				filter.setParamValues('classroomIds', [classroom.id]);
			} else {
				filter.clearParam('classroomIds');
			}

			const promise: Promise<void> = this.applyFilter(filter)
				.then(() => undefined)
				.catch((e) => {
					// Don't cache a failed load: the next call for this scope must retry.
					if (this._loadForClassroomPromise === promise) {
						this.loadedClassroomKey = null;
						this._loadForClassroomPromise = null;
					}
					throw e;
				});

			this._loadForClassroomPromise = promise;
			return promise;
		},

		/**
		 * Load *all* pupils for the given classroom (default: the current classroom;
		 * null = all classrooms) by loading the first page and then every following page.
		 * Same signature as loadForClassroom(): repeated calls for the same scope join the
		 * in-flight loop, `force` bypasses the cache (used for realtime reloads), and a call
		 * for a different scope supersedes a running loop instead of joining it.
		 * Returns the full list.
		 */
		async loadAllForClassroom(classroom?: Classroom | null, force: boolean = false): Promise<Pupil[]> {
			if (typeof (classroom) === 'undefined') {
				classroom = useClassroomsStore().currentClassroom;
			}

			const key = classroom ? `classroom:${classroom.id}` : 'all';

			if (!force && this._loadAllForClassroomPromise && this._loadAllForClassroomKey === key) {
				return this._loadAllForClassroomPromise;
			}

			this._loadAllForClassroomKey = key;

			const promise: Promise<Pupil[]> = (async () => {
				try {
					await this.loadForClassroom(classroom, force);
					this._loadAllMode = true;

					// Stop as soon as another scope (or a forced reload) took over, so this
					// loop never pushes pages of a stale scope into the new item list.
					while (
						this._loadAllForClassroomPromise === promise
						&& this.loadedClassroomKey === key
						&& this.page
						&& this.page.hasNext()
					) {
						await this.loadMore();
					}

					return this.items as Pupil[];
				} finally {
					if (this._loadAllForClassroomPromise === promise) {
						this._loadAllForClassroomPromise = null;
						this._loadAllForClassroomKey = null;
					}
				}
			})();

			this._loadAllForClassroomPromise = promise;
			return promise;
		},

		/**
		 * Resolve a pupil from (partial) API data: return the loaded store item when present,
		 * otherwise map the data, or fall back to an id-only stub.
		 */
		findByIdOrMap(data: any): Pupil {
			const pupil = this.find(data.id);
			if (pupil) {
				return pupil;
			} else if (Object.keys(data).length > 1) {
				return Pupil.mapFromServer(data);
			} else if (data.id) {
				const stub = new Pupil();
				stub.id = data.id;
				return stub;
			}

			throw new Error('Invalid pupil data provided.');
		},

		/**
		 * Search the full pupil directory (across all classrooms) by name and date of birth.
		 * Used for duplicate detection when adding a pupil to a classroom.
		 */
		async findByNameAndDateOfBirth(
			firstName: string,
			lastName: string,
			dateOfBirth: string | null
		): Promise<Pupil[]> {
			try {
				const response = await this.apiClient.get(this.apiUrl(Method.INDEX), {
					params: {
						mask: this.defaultMask.join(','),
						firstName: firstName,
						lastName: lastName,
						birthdate: dateOfBirth,
					}
				});

				return response.data.data.map((data: any) => this.mapToItem(data));
			} catch (e: any) {
				this.errors = ApiErrorMessageBag.fromAxiosException(e);
				throw this.errors;
			}
		},

		async save(pupil: Pupil) {
			await this.superSave(pupil);

			// Reload the current scope so classroom-membership changes are reflected
			// (e.g. a pupil removed from the viewed classroom drops out of the list).
			// awaitNewDataBeforeResettingList=true avoids flashing an empty list.
			await this.load(true);
		},

		async bulkSave(pupils: Pupil[], detachOtherClassrooms: boolean = false) {
			await this.superBulkSave(pupils, {
				detachOtherClassrooms: detachOtherClassrooms,
			});
			await this.load(true);
		},

		async bulkAddToClassroom(pupils: Pupil[], classroom: Classroom): Promise<void> {
			const modifiedPupils: Pupil[] = [];
			for (const pupil of pupils) {
				if (!pupil.belongsToClassroom(classroom)) {
					const cloned = pupil.clone();
					await cloned.toggleClassroom(classroom, true);
					modifiedPupils.push(cloned);
				}
			}
			if (modifiedPupils.length > 0) {
				await this.bulkSave(modifiedPupils, true);
			}
		},

		async bulkRemoveFromClassroom(pupils: Pupil[], classroom: Classroom): Promise<void> {
			const modifiedPupils: Pupil[] = [];
			for (const pupil of pupils) {
				if (pupil.belongsToClassroom(classroom)) {
					const cloned = pupil.clone();
					await cloned.toggleClassroom(classroom, false);
					modifiedPupils.push(cloned);
				}
			}
			if (modifiedPupils.length > 0) {
				await this.bulkSave(modifiedPupils, true);
			}
		},

		// Use the richer setFromModel() copy when refreshing an existing store item so we don't
		// lose inactive classroom relations the way asApiDataObject() would.
		updateStoreItemFromNewItem(existingModel: Pupil, newData: Pupil) {
			existingModel.setFromModel(newData);
		},

		// Pupils are loaded in per 50 a page and ordered client-side (incl. DateTime-aware birthdate sorting).
		shouldSortLocally(): boolean {
			return false;
		},

		/**
		 * Apply an order, persist it as the user's default (unless storePreference is false),
		 * and re-sort the loaded pupils locally.
		 */
		applyOrder(order: OrderParameter, storePreference: boolean = true) {
			this.order = order;
			if (storePreference) {
				useDefaultOrderStore().setDefaultPupilsOrder(order);
			}

			if (this._loadAllMode) {
				this.loadAllForClassroom();
			} else {
				this.loadFirstPage();
			}
		},

		/**
		 * Reset the ordering to the user's stored default and re-sort the loaded pupils.
		 */
		resetDefaultOrder() {
			this.order = useDefaultOrderStore().pupilsOrder;
			// this.sortLocally();
		},

		orderCompare(item1: Pupil, item2: Pupil, order?: OrderParameter): number {
			const o = order ?? this.order;

			let result = this._comparePupilAttribute(o.property, item1, item2);
			if (result === 0 && o.secondProperty) {
				result = this._comparePupilAttribute(o.secondProperty, item1, item2);
			}

			return o.direction === OrderDirection.DESC ? -result : result;
		},

		_comparePupilAttribute(attr: string, a: Pupil, b: Pupil): number {
			const av = (a as any)[attr];
			const bv = (b as any)[attr];

			if (!av && !bv) {
				return 0;
			} else if (!av) {
				return 1;
			} else if (!bv) {
				return -1;
			} else if (av instanceof DateTime) {
				return av.diff(bv).milliseconds;
			} else if (av !== bv) {
				return av.localeCompare(bv);
			}

			return 0;
		},

		async afterLoad(items: Pupil[]) {
			this.updateUsedIconMaps();
		},

		onStoreChanged() {
			this.updateUsedIconMaps();
		},

		/**
		 * Rebuild the icon-usage map for the classroom currently in scope.
		 * No-op when not scoped to a single classroom.
		 */
		updateUsedIconMaps() {
			this.usedIconsMap = {};

			const classroom = this.iconMapClassroom;
			if (!classroom) {
				return;
			}

			this.items.forEach((pupil: Pupil) => {
				const icon = pupil.getIconForClassroom(classroom);
				if (icon instanceof SpriteIcon) {
					this.usedIconsMap[icon.url] = pupil;
				} else if (icon instanceof FileIcon) {
					this.usedIconsMap[icon.id] = pupil;
				}
			});
		},

	},
});
